import { Plan, Limit } from "../Entities/Plan";
import { Subscription } from "../Entities/Subscription";
import { isAnnualMode } from "../Utils/generic-functions";
import { useAccessContext } from "../Context/AccessContext";

export interface CurrentAccessAddon {
  type: "job-post" | "job-application";
  quantity: number;
  plan: Plan;
}

interface CurrentAccessReturn {
  currentPlan: Plan | null;
  currentSubscription: Subscription | null;
  currentLimits: Limit | null;
  currentMode: "base" | "credit" | "free" | null;
  currentAddons: CurrentAccessAddon[];
  /** Subscription is active (not expired). For Free / Credit based / Plan based, col 1–4 assume subscription active. */
  isSubscriptionActive: boolean;
  /** Plan based (mode base) with annual billing. */
  isPlanBasedAnnual: boolean;
  /** Plan based (mode base) with monthly billing. */
  isPlanBasedMonthly: boolean;
  /** Allowed: Free, Credit based, Plan monthly, Expired. Not allowed: Plan annual. */
  canPurchaseCredits: boolean;
  /** Allowed: Free, Credit based, Expired. Not allowed: Plan monthly, Plan annual. */
  canPurchasePlan: boolean;
  /** Allowed: Plan monthly, Plan annual only. Not allowed: Free, Credit based, Expired. */
  canPurchaseAddonOnly: boolean;
  changeSubscriptionMeasurements: (
    value: number,
    type: "jobPosts" | "jobApplications" | "teamMembers"
  ) => void;
}

const useAccess = (): CurrentAccessReturn => {
    const {
      currentPlan,
      currentSubscription,
      currentLimits,
      currentMode,
      currentAddons,
      changeSubscriptionMeasurements,
    } = useAccessContext();

    const isSubscriptionActive =
      currentSubscription?.status === "active";

    const isPlanBasedAnnual =
      isSubscriptionActive &&
      currentMode === "base" &&
      !!currentSubscription &&
      isAnnualMode(currentSubscription.startDate, currentSubscription.endDate);

    const isPlanBasedMonthly =
      isSubscriptionActive &&
      currentMode === "base" &&
      !!currentSubscription &&
      !isAnnualMode(
        currentSubscription.startDate,
        currentSubscription.endDate
      );

    const canPurchaseCredits =
      !isSubscriptionActive ||
      currentMode === "free" ||
      currentMode === "credit" ||
      (currentMode === "base" && !isPlanBasedAnnual);

    const canPurchasePlan =
      !isSubscriptionActive ||
      currentMode === "free" ||
      currentMode === "credit";

    const canPurchaseAddonOnly = isSubscriptionActive && currentMode === "base";

    return {
      currentPlan,
      currentSubscription,
      currentLimits,
      currentMode,
      currentAddons,
      isSubscriptionActive,
      isPlanBasedAnnual,
      isPlanBasedMonthly,
      canPurchaseCredits,
      canPurchasePlan,
      canPurchaseAddonOnly,
      changeSubscriptionMeasurements,
    };
  };


export default useAccess;
